import moment from 'moment';
import Container from '@/components/Container';
import Link from '@/components/Link';
import ClassInfoPanel from '@/domain/Classes/ClassInfoPanel';
import ClassTrainers from '@/domain/Classes/ClassTrainers';
import ClassDatesCalendar from '@/domain/Classes/ClassDatesCalendar';
import client from '@/api/client';
import { classQuery, classSlugsQuery } from '@/api/queries';

export async function getStaticProps({ params }) {
  const slug = params.slug[params.slug.length - 1];

  const { data } = await client().query({
    query: classQuery,
    variables: {
      id: slug,
    },
  });

  return {
    props: {
      clasx: data?.class,
    },
  };
}

export async function getStaticPaths() {
  const { data } = await client().query({ query: classSlugsQuery });

  const paths = data?.classes?.nodes?.map(({ slug, date }) => {
    const d = moment(date);

    return {
      params: {
        slug: [d.format('YYYY'), d.format('MM'), d.format('DD'), slug],
      },
    };
  });

  return {
    paths,
    fallback: false,
  };
}

export default function Class({ clasx }) {
  // no class to render
  if (clasx === null) {
    return null;
  }

  const training = clasx.details?.training;
  const trainers = clasx.details?.trainers;
  const dates = clasx.details?.dates || [];

  const hasObservations = clasx.details?.observations !== null;

  return (
    <div className="relative bg-gray-100 bg-gradient-to-br from-white to-gray-100">
      <div className="relative z-20 py-10 lg:pb-20">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 lg:gap-10 xl:gap-16">
            <div className="lg:col-span-4">
              <div className="sticky top-28">
                <ClassInfoPanel
                  training={training}
                  classDetails={clasx.details}
                />
              </div>
            </div>
            <div className="space-y-4 lg:col-span-8 lg:space-y-10 lg:pr-10">
              <Link
                className="inline-flex items-center space-x-2 text-lg font-semibold text-green-500 hover:text-green-600"
                href="/clases"
              >
                <svg
                  className="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l2.293 2.293a1 1 0 010 1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>Volver al calendario de clases</div>
              </Link>
              <div className="space-y-10">
                <h1 className="text-xl font-medium lg:text-4xl lg:font-bold lg:tracking-tight">
                  {training.title}
                </h1>
                <ClassTrainers trainers={trainers} />
              </div>
              {hasObservations && (
                <div className="space-y-4 rounded-lg bg-yellow-50 px-8 py-6 shadow ring-1 ring-black ring-opacity-10 lg:rounded-xl xl:rounded-2xl">
                  <h3 className="flex items-center space-x-4">
                    <div>
                      <svg
                        className="h-6 w-6 text-green-500"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="text-xl font-semibold lg:text-2xl">
                      Indicaciones generales
                    </div>
                  </h3>
                  <div
                    className="prose lg:prose-lg"
                    dangerouslySetInnerHTML={{
                      __html: clasx.details.observations,
                    }}
                  />
                </div>
              )}
              <ClassDatesCalendar dates={dates} />
              <div
                className="prose lg:prose-lg xl:prose-xl"
                dangerouslySetInnerHTML={{ __html: clasx.content }}
              />
            </div>
          </div>
        </Container>
      </div>
      <div className="pointer-events-none absolute top-0 left-0 bottom-0 z-0 w-2/5 overflow-hidden">
        <div className="mt-20 -skew-y-6 transform">
          <div className="h-20 w-2/3 bg-gradient-to-r from-green-400 to-transparent" />
          <div className="h-20 w-2/4 bg-gradient-to-r from-green-300 to-transparent" />
          <div className="h-20 w-full bg-gradient-to-r from-green-100 to-transparent" />
          <div className="h-20 w-full bg-gradient-to-r from-green-200 to-transparent" />
        </div>
      </div>
    </div>
  );
}
